import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import { customStyles } from "../../styles/dataTableStyles";
import { api } from "../../api";

function DevcoFinancialTable() {
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialData, setFinancialData] = useState([]);
  const [financialError, setFinancialError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const token = useSelector((state) => state.auth.token);

  // Date range state
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  const fetchFinancialData = async () => {
    if (!fromDate || !toDate) {
      setFinancialError("Please select both from and to dates");
      return;
    }

    setFinancialLoading(true);
    setFinancialError(null);

    try {
      const response = await api.devco.getDevcoFinancialData(
        {
          fromDate,
          toDate,
        },
        token
      );

      if (response?.success && response.data?.CONTENT) {
        const contentData = Array.isArray(response.data.CONTENT)
          ? response.data.CONTENT
          : [];
        setFinancialData(contentData);
      } else {
        setFinancialError("Failed to load financial data");
        setFinancialData([]);
      }
    } catch (error) {
      console.error("Error fetching financial data:", error);
      setFinancialError("Failed to load financial data");
      setFinancialData([]);
    } finally {
      setFinancialLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCurrency = (value, symbol = "USD") => {
    const numValue = parseFloat(value);
    return `${symbol} ${numValue.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // DataTable columns configuration
  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.TRANSACTION_ID,
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row.TRANSACTION_Type,
      cell: (row) => (
        <span className="badge bg-info text-white">{row.TRANSACTION_Type}</span>
      ),
      sortable: true,
    },
    {
      name: "Value",
      selector: (row) => row.TRANSACTION_Value,
      cell: (row) => (
        <span
          className={
            parseFloat(row.TRANSACTION_Value) < 0
              ? "text-danger"
              : "text-success"
          }
        >
          {formatCurrency(
            row.TRANSACTION_Value,
            row.TRANSACTION_CurrencySymbol
          )}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Date Captured",
      selector: (row) => row.TRANSACTION_DateCaptured,
      cell: (row) => formatDate(row.TRANSACTION_DateCaptured),
      sortable: true,
    },
    {
      name: "Reference",
      selector: (row) => row.TRANSACTION_Reference,
      cell: (row) => (
        <span className="add-ellip" title={row.TRANSACTION_Reference}>
          {row.TRANSACTION_Reference}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Account Balance",
      selector: (row) => row.TRANSACTION_AccountBalance,
      cell: (row) => (
        <span className="text-success">
          {formatCurrency(
            row.TRANSACTION_AccountBalance,
            row.TRANSACTION_CurrencySymbol
          )}
        </span>
      ),
      sortable: true,
    },
  ];

  return (
    <div className="row mt-4">
      <div className="col-sm-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <strong>Financial Data</strong>
            </h5>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-expanded={!isCollapsed}
              style={{
                textDecoration: "none",
                outline: "none",
                border: "none",
                background: "#5C2E91",
                borderRadius: "4px",
              }}
            >
              <i
                className={`bx ${
                  isCollapsed ? "bx-chevron-down" : "bx-chevron-up"
                }`}
                style={{ color: "white" }}
              ></i>
            </button>
          </div>
          {!isCollapsed && (
            <div className="card-body">
              {/* Date Range Filters */}
              <div className="d-flex justify-content-end align-items-center mb-3 flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <label className="mr-2">From Date:</label>
                  <input
                    type="date"
                    value={fromDate}
                    className="form-control"
                    style={{ width: "200px", marginRight: "10px" }}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                  <label className="mr-2">To Date:</label>
                  <input
                    type="date"
                    value={toDate}
                    className="form-control"
                    style={{ width: "200px", marginRight: "10px" }}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  <button
                    className="btn btn-primary ml-2 px-4"
                    onClick={fetchFinancialData}
                    disabled={financialLoading}
                  >
                    {financialLoading ? "Loading..." : "Get Data"}
                  </button>
                </div>
              </div>

              {/* Error Display */}
              {financialError && (
                <div className="alert alert-danger" role="alert">
                  {financialError}
                </div>
              )}

              {/* DataTable */}
              <div className="card card-table">
                <div>
                  <div className="reports">
                    <DataTable
                      columns={columns}
                      data={Array.isArray(financialData) ? financialData : []}
                      progressPending={financialLoading}
                      highlightOnHover
                      pointerOnHover
                      customStyles={customStyles}
                      responsive
                      noHeader={false}
                      noTableHead={false}
                      wrap
                      noDataComponent={
                        <div className="text-center text-muted py-4">
                          <p>
                            No financial data found for the selected date range.
                          </p>
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevcoFinancialTable;
